import React, { useEffect, useState } from "react";
import { Popup } from "../../../../UI/Popup/Popup";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import styles from "./ConflictsFoundModal.module.css";

const TYPE_LABELS = {
  demo: "Demo conflict",
  class: "Class conflict",
  student: "Student's own class",
  studentDemo: "Student's own demo",
};

/* A demo row carries the demo's time and the booked student instead of a class
   number and a batch to open — the student's own demo (R-06-10) reads the same way. */
const isDemo = (type) => type === "demo" || type === "studentDemo";

/* The admin app is a HashRouter; the conflicting batch opens in a new tab so the
   modal stays open for the other rows (R-06-04). */
const batchHref = (batchId) =>
  `#/classes/view-batch/edit-batch/${encodeURIComponent(batchId ?? "")}`;

const CopyButton = ({ value }) => (
  <button
    type="button"
    className={styles.copy}
    title="Copy"
    onClick={() => navigator.clipboard?.writeText?.(value)}
  >
    <SvgIcons name="copy_icon" width="16" height="16" />
  </button>
);

/* The latest clash's day, bold as in the design; a refusal with no rows has none. */
const ConflictDate = ({ value }) =>
  value ? <strong className={styles.date}>{value}</strong> : "the last conflict";

const Cell = ({ label, className, children }) => (
  <div className={className ? `${styles.cell} ${className}` : styles.cell}>
    <div className={styles.cellLabel}>{label}</div>
    <div className={styles.cellValue}>{children}</div>
  </div>
);

/**
 * The calendar-conflict modal behind a `schedule-conflicts` refusal (R-06-06).
 *
 * Every row the backend found is listed: a demo, a class of another batch, or a
 * class of the student's own (R-06-10). Times are IST, as the backend sends them.
 * The backend names the first class day after the last clash it found; it is a
 * starting point, not a promise, and the resubmit is checked again. Without one,
 * the rows have to be resolved before anything can be created.
 *
 * A refusal carries `batchId` only when the plan that failed is a successor, so its
 * presence means these clashes belong to a connected batch rather than the one on
 * screen. That batch starts when this one ends, so its start cannot be moved from
 * here — the screen withholds the proposal and this names the batch instead.
 *
 * `canProceed` false is Edit's version (Figma 560:2930 "Edit batch - conflict
 * handling"): no way forward from the modal at all — the clashes are listed with
 * one note to resolve them, and Close is the only button. Add keeps the next-week
 * opt-in (Figma 334:10603), its note and label naming the latest clash's day.
 *
 * @param {{ conflicts: {batchId?: string|null,
 *             conflicts: {type: "demo"|"class"|"student"|"studentDemo", mentorName: string,
 *               at: string, ownClassAt: string, classNumber?: number|string,
 *               batchId?: string, studentEmail?: string}[],
 *             lastConflictOn?: string|null,
 *             proposedStart?: {startDate: string, projectedEnd?: string}|null}|null,
 *           batchLabel?: string, submitting?: boolean,
 *           canProceed?: boolean,
 *           onCancel: () => void, onUseProposedStart?: () => void }} props
 */
const ConflictsFoundModal = ({
  conflicts,
  batchLabel,
  submitting,
  canProceed = true,
  onCancel,
  onUseProposedStart,
}) => {
  /* The design's opt-in: moving the start is the ops user's call, so it is off
     each time the modal opens and the button waits for it. */
  const [allowNextWeek, setAllowNextWeek] = useState(false);
  useEffect(() => setAllowNextWeek(false), [conflicts]);

  if (!conflicts) return null;

  const rows = conflicts?.conflicts ?? [];
  const proposedStart = canProceed ? conflicts?.proposedStart : null;
  const connectedBatch = conflicts?.batchId ? batchLabel || conflicts?.batchId : "";
  const noProposalText = connectedBatch
    ? `${connectedBatch} starts when this batch ends, so its start date can’t be changed here. Resolve the clashes above or change this batch’s schedule to move the connected batch.`
    : "No later start date has the same weekdays available. Resolve the clashes in the batches above, then try again.";
  return (
    <Popup onClose={onCancel} contentClassName={styles.popupShell} showCloseIcon={false}>
      <div className={styles.modal} role="dialog" aria-label="Conflicts found">
        <div className={styles.header}>
          <h3 className={styles.title}>Conflicts found</h3>
          {connectedBatch && (
            <p className={styles.subtitle}>
              These clashes are on the connected batch <strong>{connectedBatch}</strong>, not on the
              one you are working on.
            </p>
          )}
        </div>

        <div className={styles.rows}>
          {rows?.map((conflict, index) => (
            <div key={index} className={styles.row}>
              <Cell label="Conflict type" className={styles.cellType}>
                {TYPE_LABELS?.[conflict?.type] ?? conflict?.type}
              </Cell>
              <Cell label="Mentor" className={styles.cellMentor}>
                {conflict?.mentorName || "\u2014"}
                {conflict?.mentorName && <CopyButton value={conflict?.mentorName} />}
              </Cell>
              <Cell
                label={isDemo(conflict?.type) ? "Demo details" : "Class details"}
                className={styles.cellDetails}
              >
                {conflict?.at} IST
              </Cell>
              {isDemo(conflict?.type) ? (
                <Cell label="Student email">
                  {conflict?.studentEmail ? (
                    <>
                      {conflict?.studentEmail}
                      <CopyButton value={conflict?.studentEmail} />
                    </>
                  ) : (
                    "No student recorded"
                  )}
                </Cell>
              ) : (
                <>
                  <Cell label="Class number" className={styles.cellNumber}>
                    {conflict?.classNumber ?? "\u2014"}
                  </Cell>
                  <Cell label="Batch ID" className={styles.cellBatch}>
                    <a
                      href={batchHref(conflict?.batchId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.batchLink}
                    >
                      {conflict?.batchId}
                    </a>
                    <CopyButton value={conflict?.batchId} />
                  </Cell>
                </>
              )}
            </div>
          ))}

          {rows?.length === 0 && (
            <p className={styles.subtitle}>
              This schedule conflicts with another class, but the conflicting class could not be
              identified.
            </p>
          )}
        </div>

        <div className={styles.footer}>
          {!canProceed ? (
            <div className={styles.resolveNote}>
              <SvgIcons name="info_circle" width="16" height="16" />
              <span>
                Please resolve all the schedule conflicts above and try submitting again.
                <br />
                Changes can&rsquo;t be saved until all conflicts are resolved.
              </span>
            </div>
          ) : proposedStart?.startDate ? (
            <>
              <div className={styles.nextWeekStrip}>
                <SvgIcons name="info_circle" width="16" height="16" />
                <span>
                  Start date will fall in the next week after{" "}
                  <ConflictDate value={conflicts?.lastConflictOn} />.
                  <br />
                  If you don&rsquo;t want to select the checkbox below, resolve all the conflicts
                  above and try again.
                </span>
              </div>
              <label className={styles.allow}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allowNextWeek}
                  onChange={(event) => setAllowNextWeek(event?.target?.checked)}
                />
                <span>
                  Allow batch creation on the next week after the conflict date. Batch start date
                  will fall after <ConflictDate value={conflicts?.lastConflictOn} />
                </span>
              </label>
            </>
          ) : (
            <div className={styles.nextWeekStrip}>
              <SvgIcons name="info_circle" width="16" height="16" />
              <span>{noProposalText}</span>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel}>
            {proposedStart?.startDate ? "Cancel" : "Close"}
          </button>
          {proposedStart?.startDate && (
            <button
              type="button"
              className={styles.create}
              disabled={submitting || !allowNextWeek}
              onClick={onUseProposedStart}
            >
              Create batch
            </button>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default ConflictsFoundModal;
